package diana.soleil.design.iraniancanadienbazzar.feature.home

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.platform.LocalUriHandler
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.NewsRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.SavedNewsController
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButtonVariant
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIconButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.BusinessCover
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.EmptyState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.ErrorState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.LoadingState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.appContentWidth
import diana.soleil.design.iraniancanadienbazzar.core.model.NewsArticle
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.home_error
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.home_retry
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.news_back_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.news_save_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.news_unsave_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.news_detail_title
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.news_image_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.news_not_found
import diana.soleil.design.iraniancanadienbazzar.feature.home.resources.news_read_more
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

data class NewsDetailUiState(
    val loading: Boolean = true,
    val article: NewsArticle? = null,
    // Distinguishes a transient load FAILURE (show Retry) from a genuine not-found (show empty).
    val error: Boolean = false,
)

/** Loads one news article for the detail screen (opened from the Home carousel). */
class NewsDetailViewModel(
    private val newsRepository: NewsRepository,
    private val savedNews: SavedNewsController,
    private val articleId: String,
) : ViewModel() {
    private val _state = MutableStateFlow(NewsDetailUiState())
    val state: StateFlow<NewsDetailUiState> = _state.asStateFlow()

    /** Whether this article is in the reading list; drives the bookmark toggle in the header. */
    val saved: StateFlow<Boolean> = savedNews.saved
        .map { articleId in it }
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), savedNews.isSaved(articleId))

    /** Save/unsave. Persists immediately and mirrors to the account for signed-in users. */
    fun toggleSaved() = savedNews.toggle(articleId)

    init {
        load()
    }

    /** (Re)fetches the article; wired to the error-state Retry. A load FAILURE shows Retry; a
     *  successful fetch that returns no article is a genuine not-found (empty state). */
    fun load() {
        _state.value = _state.value.copy(loading = true, error = false)
        viewModelScope.launch {
            _state.value = when (val result = newsRepository.getArticle(articleId)) {
                is DataResult.Success -> NewsDetailUiState(loading = false, article = result.data)
                // A missing doc (deleted article / bad deep link) is a genuine not-found → empty
                // state; only real failures (network, etc.) show the Retry branch.
                is DataResult.Failure ->
                    if (result.error is AppError.NotFound) NewsDetailUiState(loading = false, article = null)
                    else NewsDetailUiState(loading = false, error = true)
            }
        }
    }
}

@Composable
fun NewsDetailRoute(
    articleId: String,
    onBack: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: NewsDetailViewModel = koinViewModel(parameters = { parametersOf(articleId) }),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    val lang = AppTheme.language
    val uriHandler = LocalUriHandler.current

    Column(modifier = modifier.background(AppTheme.colors.background).appContentWidth(AppTheme.sizes.readingMaxWidth).fillMaxHeight()) {
        Row(
            modifier = Modifier.fillMaxWidth().statusBarsPadding().navigationBarsPadding().padding(AppTheme.spacing.lg),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
        ) {
            AppIconButton(
                iconName = "arrow_back",
                contentDescription = stringResource(Res.string.news_back_a11y),
                onClick = onBack,
                tint = AppTheme.colors.onSurface,
            )
            Text(
                text = stringResource(Res.string.news_detail_title),
                style = AppTheme.typography.headlineLg,
                color = AppTheme.colors.onSurface,
                modifier = Modifier.weight(1f),
            )
            // Only offer the bookmark once there is a real article to save — a header action that
            // does nothing while loading (or on a not-found deep link) is the dead-affordance trap.
            if (state.article != null) {
                val saved by viewModel.saved.collectAsStateWithLifecycle()
                AppIconButton(
                    // `filled` swaps to the solid Material Symbols face — a tint change alone reads as
                    // "highlighted", not "saved", and is invisible to anyone who can't distinguish the
                    // two colours.
                    filled = saved,
                    iconName = if (saved) "bookmark" else "bookmark_border",
                    contentDescription = stringResource(
                        if (saved) Res.string.news_unsave_a11y else Res.string.news_save_a11y,
                    ),
                    onClick = viewModel::toggleSaved,
                    tint = if (saved) AppTheme.colors.primary else AppTheme.colors.onSurface,
                )
            }
        }
        when {
            state.loading -> LoadingState()
            state.error -> ErrorState(
                message = stringResource(Res.string.home_error),
                onRetry = viewModel::load,
                retryLabel = stringResource(Res.string.home_retry),
            )
            state.article == null -> EmptyState(
                title = stringResource(Res.string.news_detail_title),
                description = stringResource(Res.string.news_not_found),
            )
            else -> {
                val article = state.article ?: return
                Column(
                    modifier = Modifier
                        .fillMaxSize()
                        .verticalScroll(rememberScrollState())
                        .padding(horizontal = AppTheme.spacing.lg)
                        .padding(bottom = AppTheme.spacing.xl),
                    verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.lg),
                ) {
                    if (!article.imageUrl.isNullOrBlank()) {
                        BusinessCover(
                            imageUrl = article.imageUrl,
                            iconName = "newspaper",
                            contentDescription = stringResource(Res.string.news_image_a11y),
                            modifier = Modifier
                                .fillMaxWidth()
                                .aspectRatio(16f / 9f)
                                .clip(AppTheme.shapes.card),
                        )
                    }
                    Text(
                        text = article.title.resolve(lang),
                        style = AppTheme.typography.headlineLg,
                        color = AppTheme.colors.onSurface,
                    )
                    if (article.publishedAtMillis > 0L) {
                        Text(
                            text = relativeTimeLabel(article.publishedAtMillis),
                            style = AppTheme.typography.labelSm,
                            color = AppTheme.colors.onSurfaceVariant,
                        )
                    }
                    Text(
                        text = article.body.resolve(lang).ifBlank { article.summary.resolve(lang) },
                        style = AppTheme.typography.bodyMd,
                        color = AppTheme.colors.onSurfaceVariant,
                    )
                    article.linkUrl?.takeIf { it.isNotBlank() }?.let { url ->
                        AppButton(
                            text = stringResource(Res.string.news_read_more),
                            onClick = { uriHandler.openUri(url) },
                            variant = AppButtonVariant.Secondary,
                            leadingIcon = "open_in_new",
                            modifier = Modifier.fillMaxWidth(),
                        )
                    }
                }
            }
        }
    }
}
